import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { FullRecordModal } from "./FullRecordModal";
import {
  ChangeLine,
  MODEL_ORDER,
  ModelTag,
  StateTags,
  modelLabel,
  recordLink,
} from "./recordDisplay";

const { Text } = Typography;
const DEBOUNCE_MS = 400;
const PAGE_SIZES = [50, 100, 200];

/**
 * Every document the merge touched — one row per record, EVERY field it changed as
 * « label : avant → après », and a « Fiche complète » door to the full card. Server-side
 * pagination and filters (model, text search, changed-only).
 * Props: mergeId, onOpenJournal?(seq) (passed on to the full card).
 */
export function MergeDocuments({ mergeId, onOpenJournal }) {
  const [s, setS] = useState({
    items: [],
    total: 0,
    models: [],
    page: 1,
    limit: 50,
    loading: false,
    error: null,
  });
  const [model, setModel] = useState(undefined);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [changedOnly, setChangedOnly] = useState(true);
  const [openDoc, setOpenDoc] = useState(null);
  const timer = useRef(null);

  // the search box drives `q` after a short pause, not on every keystroke
  const onText = v => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setQ(v.trim()), DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const load = useCallback(
    async (page = 1, limit = 50) => {
      setS(x => ({ ...x, loading: true, error: null }));
      try {
        const d = await mergeApi.documents(mergeId, {
          model,
          q: q || undefined,
          page,
          limit,
          // omitted (not "0") when off: a "0" string would still be truthy server-side
          changedOnly: changedOnly ? 1 : undefined,
        });
        setS({
          items: d.items || [],
          total: d.total || 0,
          models: d.models || [],
          page: d.page || page,
          limit: d.limit || limit,
          loading: false,
          error: null,
        });
      } catch (e) {
        setS(x => ({ ...x, loading: false, error: errMsg(e) }));
      }
    },
    [mergeId, model, q, changedOnly],
  );
  useEffect(() => {
    load(1, s.limit); /* eslint-disable-next-line */
  }, [load]);

  const rank = m => {
    const i = MODEL_ORDER.indexOf(m);
    return i < 0 ? 99 : i;
  };
  const modelOptions = [...(s.models || [])]
    .sort((a, b) => rank(a.model) - rank(b.model))
    .map(m => ({
      value: m.model,
      label: `${modelLabel(m.model)} (${m.count})`,
    }));

  const columns = [
    {
      title: "Enregistrement",
      dataIndex: "label",
      width: 320,
      render: (l, r) => {
        const to =
          r.exists !== false ? recordLink(r.model, r.documentId) : null;
        return (
          <Space direction="vertical" size={2}>
            {to ? (
              <Link to={to} onClick={e => e.stopPropagation()}>
                {l || r.documentId}
              </Link>
            ) : (
              <Text strong>{l || r.documentId}</Text>
            )}
            <Space size={4} wrap>
              <ModelTag model={r.model} />
              <StateTags item={r} />
            </Space>
          </Space>
        );
      },
    },
    {
      title: "Champs modifiés — avant → après",
      dataIndex: "changes",
      render: (changes, r) =>
        Array.isArray(changes) && changes.length ? (
          <div>
            {changes.map((c, i) => (
              <ChangeLine
                key={`${c.path || c.field || i}`}
                label={c.label || c.path || c.field}
                before={c.before}
                after={c.after}
              />
            ))}
          </div>
        ) : r.createdByMerge ? (
          <Text type="secondary">
            créé par la fusion — tous les champs sont nouveaux (voir la fiche)
          </Text>
        ) : r.deletedByMerge ? (
          <Text type="secondary">
            supprimé par la fusion — tous les champs figurent dans la fiche
          </Text>
        ) : (
          <Text type="secondary">aucun champ modifié</Text>
        ),
    },
    {
      title: "",
      key: "card",
      width: 140,
      render: (_, r) => (
        <Button
          size="small"
          onClick={e => {
            e.stopPropagation();
            setOpenDoc(r);
          }}
        >
          Fiche complète
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Chaque enregistrement que la fusion a touché, avec chaque champ modifié — la valeur d'avant et celle d'après. « Fiche complète » montre tous les champs du document : avant, après la fusion, et sa valeur actuelle."
      />
      <Space wrap>
        <Select
          allowClear
          placeholder="Collection"
          style={{ width: 220 }}
          value={model}
          onChange={setModel}
          options={modelOptions}
        />
        <Input.Search
          allowClear
          style={{ width: 380 }}
          placeholder="Rechercher un enregistrement (n° de prestation, n° de facture, nom, e-mail…)"
          value={text}
          onChange={e => onText(e.target.value)}
          onSearch={v => {
            clearTimeout(timer.current);
            setQ((v || "").trim());
          }}
        />
        <Radio.Group
          value={changedOnly ? "changed" : "all"}
          onChange={e => setChangedOnly(e.target.value === "changed")}
        >
          <Radio.Button value="changed">Modifiés seulement</Radio.Button>
          <Radio.Button value="all">Tous</Radio.Button>
        </Radio.Group>
        <Text type="secondary">{s.total} enregistrement(s)</Text>
      </Space>
      {s.error && (
        <Alert
          type="error"
          showIcon
          message="Liste des enregistrements indisponible"
          description={s.error}
          action={
            <Button size="small" onClick={() => load(s.page, s.limit)}>
              Réessayer
            </Button>
          }
        />
      )}
      <Table
        size="small"
        rowKey={r => `${r.model}:${r.documentId}`}
        loading={s.loading}
        dataSource={s.items}
        columns={columns}
        onRow={r => ({
          onClick: () => setOpenDoc(r),
          style: { cursor: "pointer" },
        })}
        pagination={{
          current: s.page,
          pageSize: s.limit,
          total: s.total,
          onChange: load,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZES,
          showTotal: (t, r) => `${r[0]}-${r[1]} sur ${t}`,
        }}
        locale={{
          emptyText: changedOnly
            ? "Aucun enregistrement modifié ne correspond."
            : "Aucun enregistrement ne correspond.",
        }}
      />
      {openDoc && (
        <FullRecordModal
          open
          mergeId={mergeId}
          model={openDoc.model}
          documentId={openDoc.documentId}
          label={openDoc.label}
          onClose={() => setOpenDoc(null)}
          onOpenJournal={
            onOpenJournal
              ? seq => {
                  setOpenDoc(null);
                  onOpenJournal(seq);
                }
              : undefined
          }
        />
      )}
    </Space>
  );
}
